import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import SignaturePad, { Options } from 'signature_pad';

@Component({
    selector: 'app-signature-pad',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './signature-pad.component.html',
    styleUrls: ['./signature-pad.component.scss']
})
export class AppSignaturePadComponent implements AfterViewInit, OnChanges, OnDestroy {
    @ViewChild('container', { static: true }) private containerRef!: ElementRef<HTMLDivElement>;
    @ViewChild('signatureCanvas', { static: true }) private canvasRef!: ElementRef<HTMLCanvasElement>;

    @Input() value: string | null = null;
    @Input() readonly: boolean = false;
    @Input() disabled: boolean = false;
    @Input() height: number = 200;
    @Input() minWidth: number = 2;
    @Input() maxWidth: number = 3;
    @Input() penColor: string = '#000000';
    @Input() backgroundColor: string = '#ffffff';
    @Input() velocityFilterWeight: number = 0.7;
    @Input() throttle: number = 16;
    @Input() minDistance: number = 5;

    @Output() valueChange = new EventEmitter<string>();
    @Output() drawStart = new EventEmitter<void>();
    @Output() drawEnd = new EventEmitter<string>();
    @Output() cleared = new EventEmitter<void>();

    private signaturePad?: SignaturePad;
    private resizeObserver?: ResizeObserver;
    private resizeFrameId?: number;
    private pendingValue: string = '';
    private lastValue: string = '';

    private readonly handleBeginStroke = () => {
        this.drawStart.emit();
    };

    private readonly handleEndStroke = () => {
        this.emitCurrentValue();
    };

    ngAfterViewInit(): void {
        this.pendingValue = this.normalizeValue(this.value);

        requestAnimationFrame(() => {
            this.initializeSignaturePad();
            this.observeContainer();
            this.updateInteractivity();
            void this.resizeCanvas(false);
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['value'] && !changes['value'].firstChange) {
            const nextValue = this.normalizeValue(this.value);

            if (nextValue !== this.lastValue) {
                this.pendingValue = nextValue;
                void this.syncIncomingValue();
            }
        }

        if (!this.signaturePad) {
            return;
        }

        if (changes['readonly']) {
            this.updateInteractivity();

            if (!this.readonly) {
                void this.resizeCanvas(true);
            }
        }

        if (
            changes['minWidth'] ||
            changes['maxWidth'] ||
            changes['penColor'] ||
            changes['backgroundColor'] ||
            changes['velocityFilterWeight'] ||
            changes['throttle'] ||
            changes['minDistance']
        ) {
            this.applyOptions();
            void this.resizeCanvas(true);
        }

        if (changes['height']) {
            void this.resizeCanvas(true);
        }

        if (changes['disabled']) {
            this.updateInteractivity();
        }
    }

    ngOnDestroy(): void {
        if (this.resizeFrameId !== undefined) {
            cancelAnimationFrame(this.resizeFrameId);
        }

        this.resizeObserver?.disconnect();

        if (this.signaturePad) {
            this.signaturePad.removeEventListener('beginStroke', this.handleBeginStroke as EventListener);
            this.signaturePad.removeEventListener('endStroke', this.handleEndStroke as EventListener);
            this.signaturePad.off();
        }
    }

    @HostListener('window:resize')
    onWindowResize(): void {
        if (this.readonly) {
            return;
        }

        this.scheduleResize();
    }

    get previewValue(): string {
        return this.normalizeValue(this.value) || this.lastValue || this.pendingValue;
    }

    clear(): void {
        if (!this.signaturePad) {
            this.pendingValue = '';
            this.lastValue = '';
            return;
        }

        this.signaturePad.clear();
        this.pendingValue = '';
        this.lastValue = '';
        this.valueChange.emit('');
        this.cleared.emit();
    }

    isEmpty(): boolean {
        return this.signaturePad ? this.signaturePad.isEmpty() : true;
    }

    toDataUrl(): string {
        if (!this.signaturePad || this.signaturePad.isEmpty()) {
            return '';
        }

        return this.signaturePad.toDataURL();
    }

    async load(dataUrl: string | null | undefined): Promise<void> {
        this.pendingValue = this.normalizeValue(dataUrl);
        await this.syncIncomingValue();
    }

    private initializeSignaturePad(): void {
        if (this.signaturePad) {
            return;
        }

        this.signaturePad = new SignaturePad(this.canvasRef.nativeElement, this.buildOptions());
        this.signaturePad.addEventListener('beginStroke', this.handleBeginStroke as EventListener);
        this.signaturePad.addEventListener('endStroke', this.handleEndStroke as EventListener);
    }

    private observeContainer(): void {
        if (typeof ResizeObserver === 'undefined') {
            return;
        }

        this.resizeObserver = new ResizeObserver(() => {
            this.scheduleResize();
        });

        this.resizeObserver.observe(this.containerRef.nativeElement);
    }

    private scheduleResize(): void {
        if (this.readonly) {
            return;
        }

        if (this.resizeFrameId !== undefined) {
            cancelAnimationFrame(this.resizeFrameId);
        }

        this.resizeFrameId = requestAnimationFrame(() => {
            this.resizeFrameId = undefined;
            void this.resizeCanvas(true);
        });
    }

    private async resizeCanvas(preserveValue: boolean): Promise<void> {
        if (!this.signaturePad || this.readonly) {
            return;
        }

        const container = this.containerRef.nativeElement;
        const canvas = this.canvasRef.nativeElement;
        const displayWidth = Math.max(Math.floor(container.getBoundingClientRect().width), 1);
        const displayHeight = Math.max(Math.floor(this.height), 1);
        const ratio = Math.max(window.devicePixelRatio || 1, 1);
        const pixelWidth = Math.floor(displayWidth * ratio);
        const pixelHeight = Math.floor(displayHeight * ratio);
        const snapshotValue = preserveValue ? this.getSnapshotValue() : this.pendingValue;

        if (canvas.width === pixelWidth && canvas.height === pixelHeight && canvas.style.height === `${displayHeight}px`) {
            if (snapshotValue !== this.lastValue) {
                await this.renderValue(snapshotValue);
            }

            return;
        }

        canvas.style.width = `${displayWidth}px`;
        canvas.style.height = `${displayHeight}px`;
        canvas.width = pixelWidth;
        canvas.height = pixelHeight;

        const context = canvas.getContext('2d');

        if (!context) {
            return;
        }

        context.setTransform(1, 0, 0, 1, 0, 0);
        context.scale(ratio, ratio);

        this.applyOptions();
        this.signaturePad.clear();
        await this.renderValue(snapshotValue);
        this.updateInteractivity();
    }

    private async syncIncomingValue(): Promise<void> {
        if (!this.signaturePad) {
            return;
        }

        await this.renderValue(this.pendingValue);
    }

    private async renderValue(dataUrl: string): Promise<void> {
        if (!this.signaturePad) {
            return;
        }

        this.signaturePad.clear();

        if (!dataUrl) {
            this.lastValue = '';
            return;
        }

        await this.signaturePad.fromDataURL(dataUrl, { ratio: 1 });
        this.lastValue = dataUrl;
    }

    private emitCurrentValue(): void {
        const currentValue = this.toDataUrl();

        this.pendingValue = currentValue;
        this.lastValue = currentValue;
        this.valueChange.emit(currentValue);
        this.drawEnd.emit(currentValue);
    }

    private getSnapshotValue(): string {
        if (!this.signaturePad || this.signaturePad.isEmpty()) {
            return this.pendingValue || this.lastValue;
        }

        return this.signaturePad.toDataURL();
    }

    private updateInteractivity(): void {
        if (!this.signaturePad) {
            return;
        }

        if (this.disabled || this.readonly) {
            this.signaturePad.off();
        } else {
            this.signaturePad.on();
        }
    }

    private applyOptions(): void {
        if (!this.signaturePad) {
            return;
        }

        Object.assign(this.signaturePad, this.buildOptions());
    }

    private buildOptions(): Options {
        return {
            minWidth: this.minWidth,
            maxWidth: this.maxWidth,
            penColor: this.penColor,
            backgroundColor: this.backgroundColor,
            velocityFilterWeight: this.velocityFilterWeight,
            throttle: this.throttle,
            minDistance: this.minDistance
        };
    }

    private normalizeValue(value: string | null | undefined): string {
        return value?.trim() ?? '';
    }
}